import { Button, CloseButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { isAsymmetryCalc } from '../../../../data/providers/metricsProvider';
import {
  IAsymmetryModeOption,
  IAsymmetryReportSettings,
} from '../../../../data/providers/settings/reports/asymmetryReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import {
  ICrossTestMetricSelection,
  ISelectableEntity,
  ISelectableMetric,
  ISelectableTag,
} from '../../../../data/types';
import {
  createTemplate,
  updateTemplate,
} from '../../../../data/writers/settings/reports/asymmetryReportSettings';
import { KEYS } from '../../../../keys';
import { queryHashGenerator } from '../../../../utils/queryHash';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ErrorMessage } from '../../../shared/form/ErrorMessage';
import { AsymmetrySelect } from '../../../shared/form/selects/AsymmetryModeSelect';
import { SingleTagSelect } from '../../../shared/form/selects/SingleTagSelect';
import { Step } from '../../../shared/form/Step';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { MetricSelector } from '../../utils/settings/MetricSelector';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateMetadata } from '../../utils/settings/TemplateMetadata';
import { ITemplateFormProps } from '../../utils/settings/types';

const MAX_METRICS = 6;

export const TemplateForm = (props: ITemplateFormProps): React.ReactElement => {
  const [template, setTemplate] = React.useState<
    Partial<ISettingsTemplate<IAsymmetryReportSettings>>
  >(props.template as ISettingsTemplate<IAsymmetryReportSettings>);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  /**
   * @description handles change to the currently selected template's description
   * @param description
   */
  const templateDescriptionChangeHandler = (description: string): void => {
    setTemplate({
      ...template,
      description,
    });
  };

  /**
   * @description handles change to the currently selected template's name
   * @param name
   */
  const templateNameChangeHandler = (name: string): void => {
    setTemplate({
      ...template,
      name,
    });
  };

  const leftTagChangeHandler = (leftTag: ISelectableTag): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        leftTag,
      },
    });
  };

  const rightTagChangeHandler = (rightTag: ISelectableTag): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        rightTag,
      },
    });
  };

  const modeChangeHandler = (mode: IAsymmetryModeOption): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        mode,
        metrics: filterMetricsByMode(mode, template?.settings?.metrics ?? []),
      },
    });
  };

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (props.template && props.template?.id) {
        await updateTemplate(
          template as ISettingsTemplate<IAsymmetryReportSettings>,
          props.isSelected
        );
      } else {
        await createTemplate(template);
      }

      props.onClose();
    } catch (e) {
      setLoading(false);
      setError('Something went wrong saving the template.');
    }
  };

  /**
   * @description handles changes to selected metrics
   * @param mMetrics
   */
  const metricsChangeHandler = (
    mMetrics: ICrossTestMetricSelection[]
  ): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        metrics: mMetrics.map(
          (
            m: ICrossTestMetricSelection
          ): {
            testType: ISelectableEntity;
            metric: ISelectableMetric;
            tags: ISelectableEntity[];
            queryHash: string;
            changeCutoffs?: {
              lowerCutoff: number;
              upperCutoff: number;
            };
          } => {
            return {
              testType: m.testType,
              metric: m.metric,
              tags: m.tags,
              queryHash: queryHashGenerator(
                m.testType.entity.id,
                m.tags.map((t: ISelectableEntity): string => t.entity.id)
              ),
              changeCutoffs: {
                lowerCutoff: m.changeCutoffs?.lowerCutoff ?? null,
                upperCutoff: m.changeCutoffs?.upperCutoff ?? null,
              },
            };
          }
        ),
      },
    });
  };

  const isValid = React.useMemo((): boolean => {
    if (isEmpty(template?.name)) return false;
    if (isEmpty(template?.settings)) return false;
    if (isEmpty(template?.settings?.metrics)) return false;
    if (isEmpty(template?.settings?.mode)) return false;
    if (
      template.settings.metrics.length < 0 ||
      template.settings.metrics.length > MAX_METRICS
    ) {
      return false;
    }
    if (template?.settings?.mode?.value === 'unilateral') {
      if (isEmpty(template?.settings?.leftTag)) return false;
      if (isEmpty(template?.settings?.rightTag)) return false;
    }

    return true;
  }, [JSON.stringify(template)]);

  return (
    <InnerPopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>
              {props.template
                ? `Edit ${props.template.name}`
                : 'Create Asymmetry Settings Template'}
            </h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading && <LinearLoading />}
          <Row shouldCollapse>
            <LeftColumn>
              <TemplateMetadata
                step={1}
                name={template?.name}
                description={template?.description}
                onTemplateDescriptionChange={templateDescriptionChangeHandler}
                onTemplateNameChange={templateNameChangeHandler}
              />
              <Row style={{ alignItems: 'self-start' }}>
                <FormSection style={{ flex: 1 }}>
                  <CardHeader>
                    <CardHeaderLeft>
                      <Step>2</Step>
                      <div>
                        <h6>Mode</h6>
                      </div>
                    </CardHeaderLeft>
                  </CardHeader>

                  <ControlSpacer>
                    <ControlLabel>Mode</ControlLabel>
                    <AsymmetrySelect
                      value={template?.settings?.mode}
                      onChange={modeChangeHandler}
                    />
                  </ControlSpacer>
                </FormSection>
                {template?.settings?.mode?.value === 'unilateral' && (
                  <FormSection style={{ flex: 3, marginLeft: '10px' }}>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>3</Step>
                        <div>
                          <h6>Select Left/Right Tags</h6>
                        </div>
                      </CardHeaderLeft>
                    </CardHeader>
                    <Row>
                      <ControlSpacer style={{ flex: 1 }}>
                        <ControlLabel>Left Tag</ControlLabel>
                        <SingleTagSelect
                          value={template?.settings?.leftTag}
                          onChange={leftTagChangeHandler}
                          id={KEYS.ASYMMETRY_LEFT_TAG_SELECT}
                        />
                      </ControlSpacer>
                      <ControlSpacer style={{ flex: 1, marginLeft: '5px' }}>
                        <ControlLabel>Right Tag</ControlLabel>
                        <SingleTagSelect
                          value={template?.settings?.rightTag}
                          onChange={rightTagChangeHandler}
                          id={KEYS.ASYMMETRY_RIGHT_TAG_SELECT}
                        />
                      </ControlSpacer>
                    </Row>
                  </FormSection>
                )}
              </Row>
            </LeftColumn>
            <RightColumn>
              <MetricSelector
                min={1}
                max={6}
                onMetricsChange={metricsChangeHandler}
                metrics={template?.settings?.metrics ?? []}
                step={template?.settings?.mode?.value === 'bilateral' ? 3 : 4}
                useCutoffs
                unsignedCutoffs
                excludeAsymmetry={
                  template?.settings?.mode?.value === 'unilateral'
                }
                onlyAsymmetry={template?.settings?.mode?.value === 'bilateral'}
                tagLabel={
                  template?.settings?.mode?.value === 'unilateral'
                    ? 'Additional Tags'
                    : 'Tags'
                }
              />
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </CardFooterLeft>
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading || !isValid} data-testid={KEYS.ASYMMETRY_REPORT_TEMPLATE_SAVE_BUTTON} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};

/**
 * @description filters metrics based on the mode
 * @param mode
 * @param metrics
 */
const filterMetricsByMode = (
  mode: IAsymmetryModeOption,
  metrics: ICrossTestMetricSelection[]
): ICrossTestMetricSelection[] => {
  return metrics.filter((m: ICrossTestMetricSelection): boolean => {
    switch (mode.value) {
      case 'unilateral':
        return !isAsymmetryCalc(m.metric.entity);
      case 'bilateral':
        return isAsymmetryCalc(m.metric.entity);
    }
  });
};
